import { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Doctor,
  TimeSlot,
  ProviderAvailabilityApiResponse,
} from '@/types/clinic';
import { BookingApi } from '@/utils/api';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

interface LocationState {
  selectedProvider: Doctor;
}

interface WeekDay {
  date: string;
  dayName: string;
  dayNumber: string;
}

export function DateSelectPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null
  );
  const [currentWeek, setCurrentWeek] = useState<WeekDay[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Doctor | null>(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<
    Record<string, Record<string, TimeSlot[]>>
  >({});
  const [nextStep, setNextStep] = useState<string | null>(null);
  const selectedProviderRef = useRef<Doctor | null>(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  const providerFromState = (location.state as LocationState)?.selectedProvider;

  // Helpers
  const getCookie = (name: string) => {
    const match = document.cookie.match(
      new RegExp(
        '(?:^|; )' +
          name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') +
          '=([^;]*)'
      )
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  };

  const setCookie = (name: string, value: string) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=604800`;
  };

  const toLocalISODate = useCallback((d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const isPastDate = useCallback(
    (dateStr: string) => {
      const today = new Date();
      const todayISO = toLocalISODate(today);
      return dateStr < todayISO;
    },
    [toLocalISODate]
  );

  const buildWeekFromStart = useCallback(
    (start: Date): WeekDay[] => {
      const week: WeekDay[] = [];
      const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
      for (let i = 0; i < 7; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        week.push({
          date: toLocalISODate(date),
          dayName: dayNames[i],
          dayNumber: date.getDate().toString(),
        });
      }
      return week;
    },
    [toLocalISODate]
  );

  useEffect(() => {
    selectedProviderRef.current = selectedProvider;
  }, [selectedProvider]);

  const fetchAvailabilityForRange = useCallback(
    async (rangeStartISO: string, rangeEndISO: string, doctors: Doctor[]) => {
      if (!clinicSlug || !doctors?.length) return;

      type AvailabilityResult = {
        providerId: string | number;
        byDate: Record<string, TimeSlot[]>;
        header?: string;
        apiProvider?: ProviderAvailabilityApiResponse['data']['provider'];
        nextStep?: string;
      };

      setIsLoadingAvailability(true);
      const results: AvailabilityResult[] = await Promise.all(
        doctors.map(async d => {
          const resp: ProviderAvailabilityApiResponse =
            await BookingApi.getProviderAvailability(clinicSlug, d.id, {
              start_date: rangeStartISO,
              end_date: rangeEndISO,
            });
          const byDate: Record<string, TimeSlot[]> = {};
          resp.data.availability_slots.forEach(s => {
            const slotDate = s.date;
            const slot: TimeSlot = {
              id: `${d.id}-${s.date}-${s.time}`,
              date: s.date,
              time: s.time,
              isAvailable: s.available && !isPastDate(s.date),
            };
            if (!byDate[slotDate]) byDate[slotDate] = [];
            byDate[slotDate].push(slot);
          });
          return {
            providerId: d.id,
            byDate,
            header: resp.translated_message || resp.message,
            apiProvider: resp.data.provider,
            nextStep: resp.next_step,
          };
        })
      ).finally(() => setIsLoadingAvailability(false));

      const map: Record<string, Record<string, TimeSlot[]>> = {};
      results.forEach(r => {
        map[r.providerId] = r.byDate;
      });
      setAvailableTimeSlots(map);

      // Store the next_step from the first result (assuming all providers have same next_step)
      if (results.length > 0 && results[0].nextStep) {
        setNextStep(results[0].nextStep);
      }
      // Optionally handle API header if needed in the future
      const currentSelected = selectedProviderRef.current;
      if (currentSelected) {
        const found = results.find(
          r => String(r.providerId) === String(currentSelected.id)
        );
        const fullName =
          found?.apiProvider?.full_name || found?.apiProvider?.name;
        if (fullName && fullName !== currentSelected.name) {
          setSelectedProvider(prev =>
            prev ? { ...prev, name: fullName } : prev
          );
        }
      }
    },
    [clinicSlug, isPastDate]
  );

  // Initialize provider and week when clinic data is available
  useEffect(() => {
    if (clinic && !selectedProvider) {
      const cookieProviderId = getCookie('selected_provider_id');
      let cookieProvider = cookieProviderId
        ? clinic.doctors.find(d => String(d.id) === String(cookieProviderId)) ||
          null
        : null;
      if (!cookieProvider && cookieProviderId) {
        cookieProvider = {
          id: String(cookieProviderId),
          name: `Provider ${cookieProviderId}`,
          specialization: '',
          experience: 0,
          education: '',
          bio: '',
          availableSlots: [],
        };
      }
      const defaultProvider =
        providerFromState || cookieProvider || clinic.doctors[0] || null;
      setSelectedProvider(defaultProvider);

      // Build current week starting Monday
      const today = new Date();
      const currentDay = today.getDay();
      const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay; // Adjust for Sunday
      const monday = new Date(today);
      monday.setDate(today.getDate() + mondayOffset);
      const week = buildWeekFromStart(monday);
      setCurrentWeek(week);

      const startISO =
        toLocalISODate(monday) < toLocalISODate(today)
          ? toLocalISODate(today)
          : toLocalISODate(monday);
      const endISO = week[6].date;
      const doctorsToFetch = defaultProvider
        ? [defaultProvider]
        : clinic.doctors;
      fetchAvailabilityForRange(startISO, endISO, doctorsToFetch);
    }
  }, [
    clinic,
    providerFromState,
    buildWeekFromStart,
    fetchAvailabilityForRange,
    toLocalISODate,
    selectedProvider,
  ]);

  // Theme is initialized globally in App via ThemeInitializer

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };
  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  };
  const fadeInUp = {
    initial: { opacity: 0, y: 16 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };
  const slideInLeft = {
    initial: { opacity: 0, x: -24 },
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };
  const scaleIn = {
    initial: { opacity: 0, scale: 0.96 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };
  const buttonHover = { scale: 1.02, y: -2, transition: { duration: 0.2 } };
  const buttonTap = { scale: 0.98, transition: { duration: 0.1 } };

  // Slot animation variants
  const slotVariants = {
    initial: { opacity: 0, scale: 0.8, y: 10 },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    hover: {
      scale: 1.05,
      y: -2,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  const slotContainerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  };

  const handleTimeSlotSelect = (slot: TimeSlot, doctor: Doctor) => {
    if (selectedTimeSlot?.id === slot.id) {
      // Deselect if clicking the same slot
      setSelectedTimeSlot(null);
      setSelectedDate(null);
    } else {
      // Select new slot
      setSelectedTimeSlot(slot);
      setSelectedDate(slot.date);
      setSelectedProvider(doctor);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    if (currentWeek.length === 0 || !clinic) return;

    const today = new Date();
    const currentStart = new Date(currentWeek[0].date);
    const newStart = new Date(currentStart);
    newStart.setDate(currentStart.getDate() + (direction === 'next' ? 7 : -7));

    const newWeek = buildWeekFromStart(newStart);

    // Prevent requesting a full week entirely before today
    const weekEnd = new Date(newWeek[6].date);
    const todayISO = toLocalISODate(today);
    if (toLocalISODate(weekEnd) < todayISO) {
      return; // Do not navigate into the past beyond today
    }

    setCurrentWeek(newWeek);

    const requestStartISO =
      newWeek[0].date < todayISO ? todayISO : newWeek[0].date;
    const requestEndISO = newWeek[6].date;

    // Get provider from cookie like in initial request
    const cookieProviderId = getCookie('selected_provider_id');
    let cookieProvider = cookieProviderId
      ? clinic.doctors.find(d => String(d.id) === String(cookieProviderId)) ||
        null
      : null;

    // Create fallback provider if not found in clinic data
    if (!cookieProvider && cookieProviderId) {
      cookieProvider = {
        id: String(cookieProviderId),
        name: `Provider ${cookieProviderId}`,
        specialization: '',
        experience: 0,
        education: '',
        bio: '',
        availableSlots: [],
      };
    }

    // Update selected provider to match cookie
    const providerToUse = cookieProvider || clinic.doctors[0] || null;
    setSelectedProvider(providerToUse);

    const doctorsToFetch = providerToUse ? [providerToUse] : clinic.doctors;

    // Fetch availability and update slots
    fetchAvailabilityForRange(requestStartISO, requestEndISO, doctorsToFetch);
    setSelectedTimeSlot(null);
    setSelectedDate(null);
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
  };

  const getWeekRange = () => {
    if (currentWeek.length === 0) return '';
    const start = new Date(currentWeek[0].date);
    const end = new Date(currentWeek[6].date);
    const today = new Date();

    if (start <= today && end >= today) {
      return `Today, ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
    }

    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  if (clinicLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || !clinic || !selectedProvider) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Provider not found</p>
          <button
            onClick={() => navigate(`/${clinicSlug}/provider-select`)}
            className='text-white px-6 py-3 rounded-lg hover:opacity-90'
            style={{ backgroundColor: 'var(--clinic-primary)' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col md:h-screen md:overflow-hidden'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
      transition={{ duration: 0.6 }}
    >
      {/* Header with Logo */}
      <motion.div
        className='flex-shrink-0 p-6'
        variants={slideInLeft}
        initial='initial'
        animate='animate'
      >
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div
        className='flex-1 flex flex-col px-6 py-8 overflow-hidden min-h-0 md:overflow-hidden'
        variants={staggerContainer}
        initial='initial'
        animate='animate'
      >
        <div className='max-w-6xl mx-auto w-full h-full flex flex-col overflow-hidden min-h-0 md:overflow-hidden'>
          {/* Provider Info */}
          <motion.div className='text-center my-8 lg:px-20' variants={fadeInUp}>
            <h2
              className='text-2xl lg:text-5xl font-semibold'
              style={{ color: 'var(--clinic-text)' }}
            >
              I'm now analyzing the schedule for openings. Please select an
              available dentist below.
            </h2>
          </motion.div>

          {/* Calendar Grid */}
          <motion.div
            className='flex-1 bg-white rounded-2xl shadow-sm lg:mx-20 overflow-hidden min-h-0 flex flex-col relative'
            variants={scaleIn}
          >
            {isLoadingAvailability && (
              <div className='absolute inset-0 z-10 bg-white/70 flex items-center justify-center'>
                <LoadingSpinner size='lg' text='Loading...' />
              </div>
            )}
            {/* Date Range Header */}
            <div className='flex items-center justify-center mb-4 py-4'>
              <div className='flex items-center space-x-8'>
                <motion.button
                  onClick={() => navigateWeek('prev')}
                  className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg
                    className='w-5 h-5 text-gray-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M15 19l-7-7 7-7'
                    />
                  </svg>
                </motion.button>

                <div className='text-center'>
                  <h3 className='text-base font-medium text-gray-900'>
                    {getWeekRange()}
                  </h3>
                </div>

                <motion.button
                  onClick={() => navigateWeek('next')}
                  className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg
                    className='w-5 h-5 text-gray-600'
                    fill='none'
                    stroke='var(--clinic-icon-stroke)'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                </motion.button>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className='hidden md:flex md:flex-col md:h-full'>
              {/* Day Headers */}
              <div className='flex'>
                <div className='w-56 p-4 '>
                  <span className='text-sm font-medium text-gray-900'>
                    Provider
                  </span>
                </div>
                {currentWeek.map(day => (
                  <motion.div
                    key={day.date}
                    className='flex-1 p-4 text-center'
                    variants={fadeInUp}
                  >
                    <div className='text-xs font-medium text-gray-600 uppercase'>
                      {day.dayName}
                    </div>
                    <div className='text-lg font-semibold text-gray-900 mt-1'>
                      {day.dayNumber}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Provider Rows - vertical scroll on wrapper */}
              <div
                className='flex-1 overflow-y-auto min-h-0'
                style={{ maxHeight: 'calc(100vh - 400px)' }}
              >
                {(selectedProvider ? [selectedProvider] : clinic?.doctors)?.map(
                  doctor => (
                    <motion.div
                      key={doctor.id}
                      className='border-b border-gray-200 last:border-b-0'
                      variants={fadeInUp}
                    >
                      <div className='flex'>
                        {/* Provider Info */}
                        <div className='w-56 p-4 '>
                          <div className='flex flex-col items-start gap-3 space-x-3'>
                            <div className='w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden flex-shrink-0'>
                              {doctor.image ? (
                                <img
                                  src={'/avatar.jpg'}
                                  alt={doctor.name}
                                  className='w-full h-full object-cover'
                                />
                              ) : (
                                <div
                                  className='w-full h-full flex items-center justify-center text-white font-semibold text-sm'
                                  style={{
                                    backgroundColor: 'var(--clinic-primary)',
                                  }}
                                >
                                  {doctor.name
                                    .split(' ')
                                    .map(n => n[0])
                                    .join('')}
                                </div>
                              )}
                            </div>
                            <div className='min-w-0'>
                              <p className='text-xs font-medium text-gray-500'>
                                {doctor.specialization}, DDS
                              </p>
                              <p className='font-medium text-gray-900 text-sm'>
                                {doctor.name}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Time Slots for Each Day */}
                        {currentWeek.map(day => {
                          const slotsForDay =
                            availableTimeSlots[doctor.id]?.[day.date] || [];
                          return (
                            <motion.div
                              key={day.date}
                              className='flex-1 p-3'
                              variants={slotContainerVariants}
                              initial='initial'
                              animate='animate'
                            >
                              <div className='grid grid-cols-1 gap-1'>
                                {slotsForDay.map(slot => (
                                  <motion.button
                                    key={slot.id}
                                    variants={slotVariants}
                                    initial='initial'
                                    animate='animate'
                                    whileHover={slot.isAvailable ? 'hover' : {}}
                                    whileTap={slot.isAvailable ? 'tap' : {}}
                                    onClick={() =>
                                      slot.isAvailable &&
                                      handleTimeSlotSelect(slot, doctor)
                                    }
                                    disabled={!slot.isAvailable}
                                    className={`flex whitespace-nowrap w-full px-4 py-2 justify-center items-center gap-2.5 self-stretch text-sm font-medium transition-all duration-200 rounded ${
                                      selectedTimeSlot?.id === slot.id
                                        ? 'text-white shadow-lg'
                                        : slot.isAvailable
                                          ? 'bg-gray-100 text-gray-900 hover:bg-gray-200 hover:shadow-md'
                                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                    }`}
                                    style={
                                      selectedTimeSlot?.id === slot.id
                                        ? {
                                            backgroundColor:
                                              'var(--clinic-primary)',
                                          }
                                        : {}
                                    }
                                  >
                                    {slot.isAvailable
                                      ? formatTime(slot.time)
                                      : '–'}
                                  </motion.button>
                                ))}
                                {slotsForDay.length === 0 && (
                                  <motion.div
                                    className='col-span-2 text-center text-gray-400 text-xs py-4'
                                    variants={slotVariants}
                                    initial='initial'
                                    animate='animate'
                                  >
                                    –
                                  </motion.div>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )
                )}
              </div>
            </div>

            {/* Mobile Layout */}
            <div className='md:hidden flex-1 overflow-y-auto'>
              {(selectedProvider ? [selectedProvider] : clinic?.doctors)?.map(
                doctor => (
                  <motion.div
                    key={doctor.id}
                    className='border-b border-gray-200 last:border-b-0 p-4'
                    variants={fadeInUp}
                  >
                    {/* Provider Info */}
                    <div className='flex items-center gap-3 mb-4'>
                      <div className='w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden flex-shrink-0'>
                        {doctor.image ? (
                          <img
                            src={'/avatar.jpg'}
                            alt={doctor.name}
                            className='w-full h-full object-cover'
                          />
                        ) : (
                          <div
                            className='w-full h-full flex items-center justify-center text-white font-semibold text-sm'
                            style={{
                              backgroundColor: 'var(--clinic-primary)',
                            }}
                          >
                            {doctor.name
                              .split(' ')
                              .map(n => n[0])
                              .join('')}
                          </div>
                        )}
                      </div>
                      <div className='min-w-0 flex-1'>
                        <p className='text-xs font-medium text-gray-500'>
                          {doctor.specialization}, DDS
                        </p>
                        <p className='font-medium text-gray-900 text-sm'>
                          {doctor.name}
                        </p>
                      </div>
                    </div>

                    {/* Days Grid for Mobile */}
                    <div className='space-y-4'>
                      {currentWeek.map(day => {
                        const slotsForDay =
                          availableTimeSlots[doctor.id]?.[day.date] || [];
                        return (
                          <motion.div
                            key={day.date}
                            className='bg-gray-50 rounded-lg p-3'
                            variants={slotContainerVariants}
                            initial='initial'
                            animate='animate'
                          >
                            <div className='flex items-center justify-between mb-3'>
                              <div>
                                <div className='text-xs font-medium text-gray-600 uppercase'>
                                  {day.dayName}
                                </div>
                                <div className='text-lg font-semibold text-gray-900'>
                                  {day.dayNumber}
                                </div>
                              </div>
                              <div className='text-xs text-gray-500'>
                                {
                                  slotsForDay.filter(slot => slot.isAvailable)
                                    .length
                                }{' '}
                                available
                              </div>
                            </div>

                            <div className='grid grid-cols-3 gap-2'>
                              {slotsForDay.map(slot => (
                                <motion.button
                                  key={slot.id}
                                  variants={slotVariants}
                                  initial='initial'
                                  animate='animate'
                                  whileHover={slot.isAvailable ? 'hover' : {}}
                                  whileTap={slot.isAvailable ? 'tap' : {}}
                                  onClick={() =>
                                    slot.isAvailable &&
                                    handleTimeSlotSelect(slot, doctor)
                                  }
                                  disabled={!slot.isAvailable}
                                  className={`flex whitespace-nowrap w-full px-3 py-2 justify-center items-center text-xs font-medium transition-all duration-200 rounded ${
                                    selectedTimeSlot?.id === slot.id
                                      ? 'text-white shadow-lg'
                                      : slot.isAvailable
                                        ? 'bg-white text-gray-900 hover:bg-gray-100 hover:shadow-md border border-gray-200'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-100'
                                  }`}
                                  style={
                                    selectedTimeSlot?.id === slot.id
                                      ? {
                                          backgroundColor: 'var(--clinic-main)',
                                        }
                                      : {}
                                  }
                                >
                                  {slot.isAvailable
                                    ? formatTime(slot.time)
                                    : '–'}
                                </motion.button>
                              ))}
                              {slotsForDay.length === 0 && (
                                <motion.div
                                  className='col-span-3 text-center text-gray-400 text-xs py-4'
                                  variants={slotVariants}
                                  initial='initial'
                                  animate='animate'
                                >
                                  No available slots
                                </motion.div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>

          {/* Next Button */}
          <div className='flex justify-center mt-8'>
            <motion.button
              disabled={!selectedDate || !selectedTimeSlot}
              onClick={async () => {
                if (
                  selectedDate &&
                  selectedTimeSlot &&
                  selectedProvider &&
                  clinicSlug
                ) {
                  try {
                    // Check if payment is required based on the service selection
                    const serviceId = getCookie('selected_service_id') || '0';

                    // Use clinic data from context instead of fetching again
                    if (clinic) {
                      const selectedService = clinic.services.find(
                        s => s.id === serviceId
                      );

                      // Check if payment is required based on service pre-payment requirement
                      // OR if the API response indicates next step is payment
                      const shouldCreateCheckoutSession =
                        selectedService?.pre_payment_required ||
                        nextStep === 'payment';

                      if (shouldCreateCheckoutSession) {
                        // Create checkout session for payment
                        const checkoutResult =
                          await BookingApi.createCheckoutSession(clinicSlug, {
                            service_id: parseInt(serviceId, 10),
                            success_url: `${window.location.origin}/${clinicSlug}/contact-info`,
                            cancel_url: `${window.location.origin}/${clinicSlug}/date-select`,
                          });

                        if (
                          (checkoutResult as any)?.data?.data?.checkout_session
                            ?.url
                        ) {
                          // Save checkout session ID to cookie before redirect
                          const checkoutSessionId = (checkoutResult as any).data
                            .data.checkout_session.id;
                          if (checkoutSessionId) {
                            setCookie('checkout_session_id', checkoutSessionId);
                          }

                          // Redirect directly to Stripe checkout
                          const checkoutUrl = (checkoutResult as any).data.data
                            .checkout_session.url;
                          if (checkoutUrl) {
                            window.location.href = checkoutUrl;
                            return;
                          }
                        } else {
                          console.error('Failed to create checkout session');
                          // Fallback to normal flow
                        }
                      }
                    }

                    // Save appointment date and time to cookies
                    setCookie('appointment_date', selectedDate);
                    setCookie('appointment_time', selectedTimeSlot.time);

                    // Navigate to next step (contact info)
                    navigate(`/${clinicSlug}/contact-info`, {
                      state: {
                        selectedProvider,
                        selectedDate,
                        selectedTimeSlot,
                      },
                    });
                  } catch (error) {
                    console.error(
                      'Error processing time slot selection:',
                      error
                    );
                    // Fallback to normal flow
                    setCookie('appointment_date', selectedDate);
                    setCookie('appointment_time', selectedTimeSlot.time);
                    navigate(`/${clinicSlug}/contact-info`, {
                      state: {
                        selectedProvider,
                        selectedDate,
                        selectedTimeSlot,
                      },
                    });
                  }
                }
              }}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5  font-semibold hover:opacity-90 ${
                !selectedDate || !selectedTimeSlot
                  ? 'opacity-80 cursor-not-allowed'
                  : ''
              }`}
              style={{
                backgroundColor: 'var(--clinic-secondary)',
                color: 'var(--clinic-text)',
              }}
              whileHover={selectedDate && selectedTimeSlot ? buttonHover : {}}
              whileTap={selectedDate && selectedTimeSlot ? buttonTap : {}}
            >
              Next
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <Footer mobileColumnLayout={true} />
    </motion.div>
  );
}
